<!--
  Nothing renders until the first page resolves: every section appears together, rather than the
  inbox header showing alone with a placeholder row while ordered sections are still unknown.
-->
<ng-container *ngIf="switcherView$ | async as view">
  <!--
    No cdkDropListGroup: every space list already names its siblings via dropListIds, and the group
    would additionally connect them to the section-reordering list below, letting a space be dropped
    into the list of sections.
  -->
  <div class="switcher-lists">
    <ng-container
      *ngIf="view.pinned as pinned"
      [ngTemplateOutlet]="switcherSection"
      [ngTemplateOutletContext]="{ $implicit: pinned, rowMenu: SpaceRowMenu.Unpin, view: view }"
    ></ng-container>

    <!--
      Pinned sits outside this list, so it can never be displaced from the top. Not rendered while
      empty: a zero-height flex child still takes a gap either side, so Pinned to Spaces would be
      two gaps rather than the one every other boundary gets.
    -->
    <div
      *ngIf="view.folders.length"
      class="switcher-sections"
      cdkDropList
      cdkDropListLockAxis="y"
      [cdkDropListData]="view.folders"
      (cdkDropListDropped)="dropSection($event)"
    >
      <div
        *ngFor="let folder of view.folders; trackBy: trackBySectionId"
        cdkDrag
        [cdkDragData]="folder"
        (cdkDragStarted)="onSwitcherDragStarted(SwitcherDragKind.Section)"
        (cdkDragEnded)="onSwitcherDragEnded()"
      >
        <ng-container
          [ngTemplateOutlet]="switcherSection"
          [ngTemplateOutletContext]="{
            $implicit: folder,
            rowMenu: SpaceRowMenu.RemoveFromSection,
            view: view
          }"
        ></ng-container>
      </div>
    </div>

    <app-nav-sidebar-section
      [label]="view.inbox.translateLabel ? (view.inbox.label | translate) : view.inbox.label"
      [menuTitle]="'Manage spaces' | translate"
      [menuContent]="spacesMenu"
      [sectionId]="view.inbox.id"
      [expanded]="view.inbox.expanded"
      [headerDropListId]="view.inbox.headerDropListId"
      [headerDropListConnectedTo]="view.dropListIds"
      (expandedChange)="setSectionExpanded(view.inbox.id, $event)"
      (headerDropListEntered)="onSectionHeaderEntered(view.inbox)"
      (headerDropListExited)="onSectionHeaderExited()"
      (headerDropListDropped)="dropSpaceOnSectionHeader(view.inbox, $event)"
    >
      <app-nav-sidebar-item
        *ngIf="!view.hasAnySpaces"
        [label]="'No spaces yet' | translate"
        icon="list-x"
        [bold]="true"
      ></app-nav-sidebar-item>

      <!--
        Paging is driven by this wrapper's anchors coming into view, which also covers the cases a
        scroll listener misses: a list too short to scroll, and a collapsed section — the section
        body clips to zero height, so the anchors stop intersecting and loading pauses on its own.
      -->
      <app-pencil-lazy-loading
        [isItemsLoading]="(isFetching$ | async) ?? false"
        [stopObserving]="(hasMoreSpaces$ | async) === false"
        (fetchMoreDataEvent)="loadMore()"
      >
        <ng-container
          *ngTemplateOutlet="
            droppableSpaces;
            context: {
              $implicit: view.inbox,
              showEmpty: !view.inbox.spaces.length && view.hasAnySpaces,
              rowMenu: SpaceRowMenu.Pin,
              dropListIds: view.dropListIds,
              spacesInCall: view.spacesInCall
            }
          "
        ></ng-container>

        <!-- Subsequent pages: keep the loaded rows and append a cycling status row while one lands. -->
        <app-nav-sidebar-item
          *ngIf="view.inbox.spaces.length && (isFetching$ | async)"
          [label]="(loadingPhrase$ | async) || '' | translate"
          [loading]="true"
        ></app-nav-sidebar-item>
      </app-pencil-lazy-loading>
    </app-nav-sidebar-section>
  </div>
</ng-container>

<!-- Pinned and folders render identically; only the menu and whether it can be renamed differ. -->
<ng-template #switcherSection let-section let-rowMenu="rowMenu" let-view="view">
  <app-nav-sidebar-section
    [label]="section.translateLabel ? (section.label | translate) : section.label"
    [menuTitle]="section.translateLabel ? (section.label | translate) : section.label"
    [menuContent]="orderedSectionMenu"
    [menuContext]="section"
    [renameable]="section.isFolder"
    [sectionId]="section.id"
    [expanded]="section.expanded"
    [expandable]="!!section.spaces.length"
    [headerDropListId]="section.headerDropListId"
    [headerDropListConnectedTo]="view.dropListIds"
    (renameCommitted)="renameSection(section, $event)"
    (expandedChange)="setSectionExpanded(section.id, $event)"
    (headerDropListEntered)="onSectionHeaderEntered(section)"
    (headerDropListExited)="onSectionHeaderExited()"
    (headerDropListDropped)="dropSpaceOnSectionHeader(section, $event)"
  >
    <ng-container
      *ngTemplateOutlet="
        droppableSpaces;
        context: {
          $implicit: section,
          showEmpty: false,
          rowMenu: rowMenu,
          dropListIds: view.dropListIds,
          spacesInCall: view.spacesInCall
        }
      "
    ></ng-container>
  </app-nav-sidebar-section>
</ng-template>

<ng-template
  #droppableSpaces
  let-section
  let-showEmpty="showEmpty"
  let-rowMenu="rowMenu"
  let-dropListIds="dropListIds"
  let-spacesInCall="spacesInCall"
>
  <div
    class="switcher-space-list"
    [class.switcher-space-list--empty]="showEmpty"
    cdkDropList
    [id]="section.id"
    [cdkDropListData]="section"
    [cdkDropListConnectedTo]="dropListIds"
    (cdkDropListDropped)="dropSpace($event)"
  >
    <div
      *ngFor="let space of section.spaces; trackBy: trackBySpaceId"
      class="switcher-space-list__item"
      cdkDrag
      cdkDragLockAxis="y"
      [cdkDragData]="space"
      (cdkDragStarted)="onSwitcherDragStarted()"
      (cdkDragEnded)="onSwitcherDragEnded()"
    >
      <app-nav-sidebar-item
        [label]="space.title || ('Untitled space' | translate)"
        [icon]="spacesInCall.has(space._id) ? 'hash_in_call' : 'hash'"
        [iconType]="spacesInCall.has(space._id) ? IconTypes.svgIcon : IconTypes.lucideIcon"
        [active]="(currentSpaceId$ | async) === space._id"
        [menuContent]="spaceRowMenu"
        [menuContext]="{ space: space, variant: rowMenu }"
        (itemClick)="onSwitcherSpaceClick(space._id)"
      ></app-nav-sidebar-item>
    </div>
  </div>
</ng-template>

<!--
  One template for every space row: the primary action differs per section, but the Move Space
  submenu below it must live in *this* view. An embedded view resolves DI from where it was
  declared, and nav-sidebar-item supplies MAT_MENU_PANEL to the view it renders here — a submenu
  trigger in a template declared elsewhere would bypass that view and open over the menu instead of
  beside it.
-->
<ng-template #spaceRowMenu let-ctx>
  <app-pencil-menu-item
    *ngIf="ctx.variant === SpaceRowMenu.Pin"
    [label]="'Pin space' | translate"
    icon="pin"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    (clickEvent)="pinSpace(ctx.space)"
  ></app-pencil-menu-item>

  <app-pencil-menu-item
    *ngIf="ctx.variant === SpaceRowMenu.Unpin"
    [label]="'Unpin space' | translate"
    icon="pin-off"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    (clickEvent)="unpinSpace(ctx.space)"
  ></app-pencil-menu-item>

  <app-pencil-menu-item
    *ngIf="ctx.variant === SpaceRowMenu.RemoveFromSection"
    [label]="'Remove from section' | translate"
    icon="list-x"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    (clickEvent)="removeSpaceFromSection(ctx.space)"
  ></app-pencil-menu-item>

  <!-- Menu route to what dragging does, for when the target is collapsed or scrolled away. -->
  <ng-container *ngIf="moveTargets$ | async as targets">
    <ng-container *ngIf="targets.length">
      <!--
        On small screens this menu is itself a bottom sheet, which can't host a nested mat-menu, so
        the sections are listed inline rather than behind a submenu.
      -->
      <ng-container *ngIf="renderMenuAsBottomSheet$ | async; else moveSpaceSubmenu">
        <app-pncl-menu-divider></app-pncl-menu-divider>
        <app-pncl-menu-header [title]="'Sections' | translate | uppercase"></app-pncl-menu-header>
        <ng-container
          *ngTemplateOutlet="moveSpaceTargets; context: { $implicit: ctx.space, targets: targets }"
        ></ng-container>
      </ng-container>

      <!-- Mirrors the board settings menu's "Move to folder" trigger. -->
      <ng-template #moveSpaceSubmenu>
        <div [matMenuTriggerFor]="moveSpaceMenu" #moveSpaceTrigger="matMenuTrigger" mat-menu-item>
          <app-pencil-menu-item
            class="switcher-menu-item--unfiltered-icon"
            [forMenu]="true"
            [label]="'Move Space' | translate"
            icon="hash_move"
            [iconConfig]="{ type: IconTypes.svgIcon }"
            (clickEvent)="openSubMenu($event, moveSpaceTrigger)"
          ></app-pencil-menu-item>
        </div>

        <mat-menu #moveSpaceMenu [hasBackdrop]="true" class="pencil-mat-menu light-scheme">
          <app-pncl-menu-header [title]="'Sections' | translate | uppercase"></app-pncl-menu-header>
          <ng-container
            *ngTemplateOutlet="
              moveSpaceTargets;
              context: { $implicit: ctx.space, targets: targets }
            "
          ></ng-container>
        </mat-menu>
      </ng-template>
    </ng-container>
  </ng-container>

  <ng-container
    *ngTemplateOutlet="deleteMenuItem; context: { $implicit: ctx.space }"
  ></ng-container>
</ng-template>

<ng-template #moveSpaceTargets let-space let-targets="targets">
  <app-pencil-menu-item
    *ngFor="let target of targets; trackBy: trackBySectionId"
    [label]="target.translateLabel ? (target.label | translate) : target.label"
    (clickEvent)="moveSpaceToSection(space, target)"
  ></app-pencil-menu-item>
</ng-template>

<ng-template #deleteMenuItem let-space>
  <app-pncl-menu-divider></app-pncl-menu-divider>
  <div
    [appTooltip]="space | cannotDeleteSpaceReason"
    [appTooltipPlacement]="'center-end'"
    [appTooltipArrow]="'left'"
    [appTooltipWrapText]="true"
    [appTooltipDisabled]="!(space | cannotDeleteSpaceReason)"
  >
    <app-pencil-menu-item
      [isDanger]="true"
      icon="delete_outline"
      [iconConfig]="{ type: IconTypes.matIcon }"
      [label]="'Delete Space' | translate"
      [disabled]="!!(space | cannotDeleteSpaceReason)"
      (clickEvent)="deleteSpace(space)"
    ></app-pencil-menu-item>
  </div>
</ng-template>

<ng-template #orderedSectionMenu let-section>
  <app-pencil-menu-item
    *ngIf="section.isFolder"
    [label]="'Rename section' | translate"
    icon="pencil"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    (clickEvent)="startSectionRename(section)"
  ></app-pencil-menu-item>

  <app-pencil-menu-item
    [isDanger]="true"
    icon="delete_outline"
    [iconConfig]="{ type: IconTypes.matIcon }"
    [label]="'Delete section' | translate"
    (clickEvent)="deleteSection(section)"
  ></app-pencil-menu-item>
</ng-template>

<ng-template #spacesMenu>
  <app-pencil-menu-item
    [label]="'Create a Space' | translate"
    icon="plus"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    [showSpinner]="(creatingSpace$ | async) ?? false"
    (clickEvent)="createSpace()"
  ></app-pencil-menu-item>
  <app-pencil-menu-item
    class="switcher-menu-item--unfiltered-icon"
    [label]="'View all Spaces' | translate"
    icon="hash_search"
    [iconConfig]="{ type: IconTypes.svgIcon }"
    (clickEvent)="viewAllSpaces()"
  ></app-pencil-menu-item>
  <!-- Set apart below the divider: this one organises the sidebar rather than acting on spaces. -->
  <app-pncl-menu-divider></app-pncl-menu-divider>
  <app-pencil-menu-item
    [label]="'Create a section' | translate"
    icon="list-plus"
    [iconConfig]="{ type: IconTypes.lucideIcon }"
    (clickEvent)="createFolder()"
  ></app-pencil-menu-item>
</ng-template>
